// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Language Context
// Manages Arabic/English switching with RTL/LTR direction
// ══════════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getTranslations } from '@constants/i18n';

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return 'ar';
    return localStorage.getItem('seldor-lang') || 'ar';
  });

  const t = useMemo(() => getTranslations(lang), [lang]);

  useEffect(() => {
    localStorage.setItem('seldor-lang', lang);
    document.documentElement.dir = t.dir;
    document.documentElement.lang = lang;
  }, [lang, t.dir]);

  const toggleLang = () => setLang(prev => (prev === 'ar' ? 'en' : 'ar'));

  const value = useMemo(() => ({
    lang, setLang, toggleLang, t, dir: t.dir, isRTL: t.dir === 'rtl'
  }), [lang, t]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used inside LangProvider');
  return ctx;
};
